import { useState, useCallback, useRef, useEffect } from "react";
import { analyzeStageRisksStream } from "../services/riskStreamApi";
import {
  initializeRiskPhaseState,
  updateRiskPhaseState,
  isRiskAnalysisComplete,
  getActiveRiskPhase,
  calculateRiskAnalysisProgress,
  extractStageRiskMetrics,
  extractRecommendations,
  getAllDetectedRisks,
  getAllDetectedErrors,
  getAllMissingEvidence,
  getAllMissingTasks,
  getAllIncompleteCoverage,
  getCoverageScore,
} from "../utils/riskStreamingEventParser";

/**
 * Custom hook for managing stage risk analysis streaming
 *
 * Handles SSE connection lifecycle, event parsing, state management,
 * and provides clean interface for React components.
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number to analyze
 * @returns {object} Hook state and methods
 */
export function useStageRiskAnalysis(workflowId, stageNumber) {
  // Streaming state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  // Phase state
  const [phaseState, setPhaseState] = useState(() => initializeRiskPhaseState());
  const [activePhase, setActivePhase] = useState(null);
  const [overallProgress, setOverallProgress] = useState(0);

  // All received events (for export/debugging)
  const [allEvents, setAllEvents] = useState([]);

  // Analysis metadata
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [analysisDuration, setAnalysisDuration] = useState(null);

  // Risk analysis results
  const [riskMetrics, setRiskMetrics] = useState({
    stage_risk_score: null,
    stage_health: "unknown",
    critical_count: 0,
    high_risk_count: 0,
    error_count: 0,
  });
  const [recommendations, setRecommendations] = useState([]);
  const [detectedRisks, setDetectedRisks] = useState([]);
  const [detectedErrors, setDetectedErrors] = useState([]);
  const [missingEvidence, setMissingEvidence] = useState([]);
  const [missingTasks, setMissingTasks] = useState([]);
  const [incompleteCoverage, setIncompleteCoverage] = useState([]);
  const [coverageScore, setCoverageScore] = useState(null);

  // Stream controller ref (for cleanup)
  const streamControllerRef = useRef(null);

  /**
   * Handle incoming SSE event
   */
  const handleEvent = useCallback(
    (event) => {
      // Store raw event
      setAllEvents((prev) => [...prev, event]);

      // Update phase state
      setPhaseState((prevState) => {
        const newState = updateRiskPhaseState(prevState, event);

        // Update active phase
        const active = getActiveRiskPhase(newState);
        setActivePhase(active);

        // Update overall progress
        const progress = calculateRiskAnalysisProgress(newState);
        setOverallProgress(progress);

        // Extract final metrics if aggregation phase is complete
        if (event.phase === 5 && event.event_type === "aggregation_complete") {
          const metrics = extractStageRiskMetrics(newState);
          setRiskMetrics(metrics);
        }

        // Extract recommendations if recommendations phase is complete
        if (event.phase === 6 && event.event_type === "recommendations_complete") {
          const recs = extractRecommendations(newState);
          setRecommendations(recs);
        }

        // Extract all detected items
        setDetectedRisks(getAllDetectedRisks(newState));
        setDetectedErrors(getAllDetectedErrors(newState));
        setMissingEvidence(getAllMissingEvidence(newState));
        setMissingTasks(getAllMissingTasks(newState));
        setIncompleteCoverage(getAllIncompleteCoverage(newState));
        setCoverageScore(getCoverageScore(newState));

        return newState;
      });

      // Handle analysis complete
      if (isRiskAnalysisComplete(event)) {
        const completionTime = new Date();
        setEndTime(completionTime);

        if (startTime) {
          const duration = completionTime - startTime;
          setAnalysisDuration(duration);
        }

        setIsAnalyzing(false);
        setIsConnected(false);
      }

      // Handle errors
      if (event.event_type === "error") {
        setError(event.error || "An error occurred during analysis");
      }
    },
    [startTime]
  );

  /**
   * Handle streaming errors
   */
  const handleError = useCallback((err) => {
    console.error("[RiskAnalysisHook] Streaming error:", err);
    setError(err.message || "Streaming connection failed");
    setIsAnalyzing(false);
    setIsConnected(false);
  }, []);

  /**
   * Handle connection opened
   */
  const handleConnectionOpen = useCallback(() => {
    setIsConnected(true);
  }, []);

  /**
   * Handle streaming complete
   */
  const handleComplete = useCallback(() => {
    setIsAnalyzing(false);
    setIsConnected(false);

    // Set end time if not already set
    if (!endTime) {
      const completionTime = new Date();
      setEndTime(completionTime);

      if (startTime) {
        const duration = completionTime - startTime;
        setAnalysisDuration(duration);
      }
    }
  }, [endTime, startTime]);

  /**
   * Start risk analysis
   */
  const startAnalysis = useCallback(
    async (userQuery = "Analyze stage risks and errors", enableWebValidation = false) => {
      // Reset state
      setIsAnalyzing(true);
      setIsConnected(false);
      setError(null);
      setPhaseState(initializeRiskPhaseState());
      setActivePhase(null);
      setOverallProgress(0);
      setAllEvents([]);
      setRiskMetrics({
        stage_risk_score: null,
        stage_health: "unknown",
        critical_count: 0,
        high_risk_count: 0,
        error_count: 0,
      });
      setRecommendations([]);
      setDetectedRisks([]);
      setDetectedErrors([]);
      setMissingEvidence([]);
      setMissingTasks([]);
      setIncompleteCoverage([]);
      setCoverageScore(null);

      const analysisStartTime = new Date();
      setStartTime(analysisStartTime);
      setEndTime(null);
      setAnalysisDuration(null);

      try {
        // Start streaming
        const controller = analyzeStageRisksStream(workflowId, stageNumber, {
          userQuery,
          enableWebValidation,
          onEvent: handleEvent,
          onError: handleError,
          onComplete: handleComplete,
          onConnectionOpen: handleConnectionOpen,
        });

        // Store controller for cleanup
        streamControllerRef.current = controller;
      } catch (err) {
        handleError(err);
      }
    },
    [workflowId, stageNumber, handleEvent, handleError, handleComplete, handleConnectionOpen]
  );

  /**
   * Stop risk analysis
   */
  const stopAnalysis = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }

    setIsAnalyzing(false);
    setIsConnected(false);

    // Calculate duration
    if (startTime && !endTime) {
      const stopTime = new Date();
      setEndTime(stopTime);
      const duration = stopTime - startTime;
      setAnalysisDuration(duration);
    }
  }, [startTime, endTime]);

  /**
   * Reset analysis state
   */
  const resetAnalysis = useCallback(() => {
    // Stop any active streaming
    if (isAnalyzing) {
      stopAnalysis();
    }

    // Reset all state
    setIsAnalyzing(false);
    setIsConnected(false);
    setError(null);
    setPhaseState(initializeRiskPhaseState());
    setActivePhase(null);
    setOverallProgress(0);
    setAllEvents([]);
    setRiskMetrics({
      stage_risk_score: null,
      stage_health: "unknown",
      critical_count: 0,
      high_risk_count: 0,
      error_count: 0,
    });
    setRecommendations([]);
    setDetectedRisks([]);
    setDetectedErrors([]);
    setMissingEvidence([]);
    setMissingTasks([]);
    setIncompleteCoverage([]);
    setCoverageScore(null);
    setStartTime(null);
    setEndTime(null);
    setAnalysisDuration(null);
  }, [isAnalyzing, stopAnalysis]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }
    };
  }, []);

  /**
   * Get phase data by phase ID
   */
  const getPhaseData = useCallback(
    (phaseId) => {
      return phaseState[phaseId] || null;
    },
    [phaseState]
  );

  /**
   * Check if analysis is complete successfully (not stopped due to error)
   */
  const isComplete = useCallback(() => {
    // Not complete if still analyzing
    if (isAnalyzing) {
      return false;
    }

    // Not complete if no end time
    if (!endTime) {
      return false;
    }

    // Check if any phase has ERROR status
    const hasError = Object.values(phaseState).some((phase) => phase.status === "error");

    // Only complete if ended without errors
    return !hasError;
  }, [isAnalyzing, endTime, phaseState]);

  /**
   * Get formatted duration
   */
  const getFormattedDuration = useCallback(() => {
    if (!analysisDuration) {
      return "-";
    }

    const seconds = Math.floor(analysisDuration / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  }, [analysisDuration]);

  /**
   * Get health status color
   */
  const getHealthColor = useCallback(() => {
    const health = riskMetrics.stage_health.toLowerCase();

    if (health === "excellent" || health === "good") {
      return "success";
    } else if (health === "fair" || health === "moderate") {
      return "warning";
    } else if (health === "poor" || health === "critical") {
      return "error";
    } else {
      return "default";
    }
  }, [riskMetrics]);

  /**
   * Get risk score color
   */
  const getRiskScoreColor = useCallback(() => {
    const score = riskMetrics.stage_risk_score;

    if (score === null) {
      return "default";
    }

    if (score < 30) {
      return "success";
    } else if (score < 60) {
      return "warning";
    } else {
      return "error";
    }
  }, [riskMetrics]);

  return {
    // State
    isAnalyzing,
    isConnected,
    error,
    phaseState,
    activePhase,
    overallProgress,
    allEvents,
    startTime,
    endTime,
    analysisDuration,

    // Risk analysis results
    riskMetrics,
    recommendations,
    detectedRisks,
    detectedErrors,
    missingEvidence,
    missingTasks,
    incompleteCoverage,
    coverageScore,

    // Methods
    startAnalysis,
    stopAnalysis,
    resetAnalysis,
    getPhaseData,
    isComplete,
    getFormattedDuration,
    getHealthColor,
    getRiskScoreColor,
  };
}

export default useStageRiskAnalysis;
